import { useCallback, useEffect, useRef, useState } from 'react';
import { Package } from 'lucide-react';
import { Sizes } from 'enums/sizes';
import { LightContractCard } from 'features/subscription-process/contractualization-process/components/cards/LightContractCard';
import { CardState } from 'features/subscription-process/contractualization-process/types/cardState';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { useSingleFlight } from 'hooks/useSingleFlight';
import { NsKey } from 'enums/NsKey';
import { useGroupContractProcess } from '../../contexts/GroupContractProcessContext';
import { ProductsSelector, ProductsSelectorHandle } from '../../selectors/ProductsSelector';
import { BrokersSelector, BrokersSelectorHandle } from '../../selectors/BrokersSelector';
import { ContractDetailLockedSummary } from './ContractDetailLockedSummary';
import { ViewDetailsButton } from '../../components/ViewDetailsButton';

/**
 * Contenu du panel d'édition (Produits + Courtiers admin-only).
 * Sépare l'enregistrement par section pour pouvoir ne déclencher que ce qui a changé.
 */
interface EditPanelContentProps {
  groupContractId: string;
  isAdmin: boolean;
  productsRef: React.RefObject<ProductsSelectorHandle>;
  brokersRef: React.RefObject<BrokersSelectorHandle>;
  onProductsValidityChange: (isValid: boolean) => void;
}

const EditPanelContent = ({
  groupContractId,
  isAdmin,
  productsRef,
  brokersRef,
  onProductsValidityChange,
}: EditPanelContentProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  return (
    <div className="flex flex-col gap-8">
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.TITLE')}
        </h3>
        <ProductsSelector
          ref={productsRef}
          groupContractId={groupContractId}
          onValidityChange={onProductsValidityChange}
        />
      </section>

      {isAdmin && (
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-base font-semibold text-gray-900 mb-3">
            {t('GROUP_CONTRACT_PROCESS.BROKERS_STEP.TITLE')}
          </h3>
          <BrokersSelector ref={brokersRef} groupContractId={groupContractId} />
        </section>
      )}
    </div>
  );
};

export const ContractDetailCard = () => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  // Le contrat vient du contexte : une seule requête pour toutes les cards,
  // rafraîchie en place par refresh() (aucun fetch local).
  const { groupContractId, contract, isLocked, refresh } = useGroupContractProcess();
  const { openPanel, closePanel, setPanelActions } = usePanel();
  // Deux capacités explicites (matrice centrale) — pas de déduction par négation :
  // gestion des courtiers du contrat groupe = admin ; variante courtier = Courtier.
  const isAdmin = useRoleCan(Capability.GroupContractManageBrokers);

  const loading = contract == null;
  const productsRef = useRef<ProductsSelectorHandle>(null);
  const brokersRef = useRef<BrokersSelectorHandle>(null);
  const [productsValid, setProductsValid] = useState(false);

  const activeTunnels = (contract?.tunnels ?? []).filter(t => t.isActive);
  const activeBrokers = (contract?.brokers ?? []).filter(b => b.isActive);

  // État de la card : VALID si au moins 1 produit, sinon CRITICAL.
  const cardState = activeTunnels.length > 0 ? CardState.VALID : CardState.CRITICAL;

  // Subtitle = résumé compact "X produit(s) · Y courtier(s)".
  const subtitle = !loading && contract
    ? t('GROUP_CONTRACT_PROCESS.DETAIL_CARD.SUBTITLE', {
        productsCount: activeTunnels.length,
        brokersCount: activeBrokers.length,
      })
    : undefined;

  const saveSelectors = useCallback(async () => {
    if (productsRef.current) await productsRef.current.save();
    if (isAdmin && brokersRef.current) await brokersRef.current.save();
    await refresh();
    closePanel();
  }, [isAdmin, refresh, closePanel]);

  // Un seul save en vol : les soumissions répétées du panel sont ignorées
  // (sinon POST /tunnels + habilitations concurrents → conflit côté back).
  const { run: handleSave, isInFlight: isSaving } = useSingleFlight(saveSelectors);

  // Garde les actions du panel à jour quand validité ou état save change.
  useEffect(() => {
    setPanelActions(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        onSubmit: handleSave,
        submitDisabled: !productsValid || isSaving,
        submitLabel: isSaving
          ? t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVING')
          : t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVE'),
      };
    });
  }, [productsValid, isSaving, handleSave, setPanelActions, t]);

  const handleOpen = useCallback(() => {
    // Verrouillé → panel read-only (résumé), sans footer d'actions.
    // Fermeture via la croix du header.
    if (isLocked) {
      openPanel(
        {
          title: t('GROUP_CONTRACT_PROCESS.DETAIL_CARD.PANEL_TITLE'),
          size: Sizes['3xl'],
        },
        <ContractDetailLockedSummary groupContractId={groupContractId} isAdmin={isAdmin} />,
      );
      setPanelActions(undefined);
      return;
    }

    openPanel(
      {
        title: t('GROUP_CONTRACT_PROCESS.DETAIL_CARD.PANEL_TITLE'),
        size: Sizes['3xl'],
      },
      <EditPanelContent
        groupContractId={groupContractId}
        isAdmin={isAdmin}
        productsRef={productsRef}
        brokersRef={brokersRef}
        onProductsValidityChange={setProductsValid}
      />,
    );
    setPanelActions({
      onSubmit: handleSave,
      submitLabel: t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVE'),
      closeLabel: t('GROUP_CONTRACT_PROCESS.ACTIONS.CANCEL'),
      submitDisabled: true, // sera mis à jour par useEffect quand productsValid change
    });
  }, [isLocked, openPanel, setPanelActions, groupContractId, isAdmin, handleSave, t]);

  return (
    <LightContractCard
      className={isLocked ? 'hover:!shadow-none' : undefined}
      icon={<Package className="w-7 h-7 text-primary" />}
      title={t('GROUP_CONTRACT_PROCESS.DETAIL_CARD.TITLE')}
      subtitle={subtitle}
      status={cardState}
      onClick={isLocked ? undefined : handleOpen}
      titleAsideOverride={isLocked ? <ViewDetailsButton onClick={handleOpen} /> : undefined}
    />
  );
};
